import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import ApproachFlow from "@/components/ApproachFlow";
import { Reveal } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import { SERVICES, APPROACH_STEPS } from "@/data/content";

export default function Consulting() {
  useTitle("Consulting — Operating Reality");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="consulting-page">
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-30" />
        <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-signal/10 blur-[130px]" />
        <div className="wrap relative py-24 lg:py-36">
          <Reveal>
            <p className="kicker">Consulting</p>
            <h1 className="mt-6 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              When the technology isn't the problem.
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 max-w-2xl space-y-5 text-base leading-relaxed text-slate-400 sm:text-lg">
              <p>
                Organisations often know that something isn't working. Processes are slow.
                Decisions get stuck. Knowledge sits with one person. Teams depend on
                spreadsheets, email and manual coordination.
              </p>
              <p>The instinct is often to buy another tool.</p>
              <p className="text-slate-200">Sometimes the real problem is somewhere else.</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact" className="btn-primary mt-12" data-testid="consulting-hero-cta">
              Start a conversation <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="wrap py-24 lg:py-32">
        <SectionHeader
          kicker="Services"
          title="Five ways in. One underlying method."
        />
        <div className="mt-14 border-b border-line/50">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <div
                className="group grid gap-4 border-t border-line/50 py-8 transition-colors duration-300 hover:bg-charcoal/40 md:grid-cols-12 md:items-baseline lg:py-10"
                data-testid={`service-row-${s.id}`}
              >
                <span className="font-mono text-xs tracking-[0.3em] text-glow/70 md:col-span-1">{s.index}</span>
                <h3 className="font-mono text-sm uppercase tracking-[0.25em] text-slate-100 transition-colors duration-300 group-hover:text-glow md:col-span-4">
                  {s.title}
                </h3>
                <p className="max-w-2xl text-sm leading-relaxed text-slate-400 md:col-span-7">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-line/50 bg-charcoal/20">
        <div className="wrap py-24 lg:py-32">
          <SectionHeader
            kicker="The approach"
            title="Problem → System → Root cause → Better operating model."
          />
          <div className="mt-16 lg:mt-20">
            <ApproachFlow steps={APPROACH_STEPS} />
          </div>
        </div>
      </section>

      <section className="border-t border-line/50">
        <div className="wrap py-24 lg:py-32">
          <Reveal className="max-w-3xl">
            <p className="kicker">Start here</p>
            <h2 className="mt-6 text-balance text-3xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Have a problem you can't quite explain?
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              If you know something isn't working but aren't sure whether the problem is
              process, people, technology or governance — let's investigate it.
            </p>
            <Link to="/contact" className="btn-primary mt-10" data-testid="consulting-bottom-cta">
              Start a conversation <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
